import React from 'react';
import { motion } from 'framer-motion';
import { Check, Zap, ArrowRight } from 'lucide-react';
import { ActivityBadge } from './ActivityBadge';
export function Hero() {
  return <section className="relative min-h-screen pt-32 pb-20 overflow-hidden flex items-center" data-id="element-95">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-blueprint opacity-40" data-id="element-96" />
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#2F80ED]/20 rounded-full blur-[140px] pointer-events-none" data-id="element-97" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#C0C0C0]/10 rounded-full blur-[120px] pointer-events-none" data-id="element-98" />

      <div className="container mx-auto px-6 relative z-10" data-id="element-99">
        <div className="max-w-5xl mx-auto flex flex-col items-center text-center" data-id="element-100">
          {/* Badge */}
          <motion.div initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-panel border-[#2F80ED]/30 text-[#2F80ED] text-sm font-semibold mb-8" data-id="element-101">
            <Zap className="w-4 h-4" data-id="element-102" />
            <span data-id="element-103">NUEVA COLECCIÓN PROFESIONAL</span>
          </motion.div>

          {/* Activity Badge */}
          <motion.div initial={{
          opacity: 0,
          y: -6
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.15
        }} className="mb-8" data-id="element-104">
            <ActivityBadge data-id="element-105" />
          </motion.div>

          {/* Main Headline */}
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.1
        }} className="text-4xl md:text-5xl lg:text-6xl font-bold font-heading leading-[1.1] mb-12 max-w-4xl" data-id="element-106">
            Dejá de perder tiempo, material y dinero:{' '}
            <span className="metallic-text" data-id="element-107">
              aprendé a construir proyectos profesionales
            </span>{' '}
            con perfiles de aluminio desde cero
          </motion.h1>

          {/* Hero Image — 3 Book Collection */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 1,
          delay: 0.2
        }} className="relative w-full max-w-4xl mb-10" data-id="element-108">
            {/* Ambient glows behind books */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-[#2F80ED]/30 rounded-full blur-[120px] pointer-events-none" data-id="element-109" />
            <div className="absolute top-1/3 left-1/4 w-64 h-64 bg-[#C0C0C0]/15 rounded-full blur-[80px] pointer-events-none" data-id="element-110" />
            <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-[#2F80ED]/20 rounded-full blur-[80px] pointer-events-none" data-id="element-111" />

            {/* Floating Profile — left side */}
            <motion.div animate={{
            y: [-15, 15, -15],
            rotateZ: [0, -6, 0]
          }} transition={{
            duration: 7,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1.5
          }} className="absolute -left-4 lg:-left-12 top-1/3 w-14 h-44 metallic-gradient opacity-70 shadow-[0_0_30px_rgba(192,192,192,0.2)] rounded-sm hidden md:block" style={{
            clipPath: 'polygon(20% 0%, 80% 0%, 100% 20%, 100% 100%, 80% 100%, 20% 100%, 0% 100%, 0% 20%)'
          }} data-id="element-112" />

            {/* Floating Profile — right side */}
            <motion.div animate={{
            y: [15, -15, 15],
            rotateZ: [0, 6, 0]
          }} transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 2
          }} className="absolute -right-4 lg:-right-12 bottom-1/4 w-14 h-44 metallic-gradient opacity-70 shadow-[0_0_30px_rgba(192,192,192,0.2)] rounded-sm hidden md:block" style={{
            clipPath: 'polygon(20% 0%, 80% 0%, 100% 20%, 100% 100%, 80% 100%, 20% 100%, 0% 100%, 0% 20%)'
          }} data-id="element-113" />

            {/* Sparkle particles */}
            {[{
            top: '10%',
            left: '15%',
            delay: 0
          }, {
            top: '70%',
            right: '10%',
            delay: 1.5
          }, {
            top: '30%',
            left: '8%',
            delay: 0.8
          }, {
            top: '80%',
            left: '40%',
            delay: 2.2
          }, {
            top: '20%',
            right: '20%',
            delay: 1.2
          }].map((p, i) => <motion.div key={i} animate={{
            opacity: [0, 1, 0],
            scale: [0.5, 1.2, 0.5]
          }} transition={{
            duration: 3,
            repeat: Infinity,
            delay: p.delay,
            ease: 'easeInOut'
          }} className="absolute w-1.5 h-1.5 rounded-full bg-[#2F80ED] shadow-[0_0_10px_#2F80ED]" style={p} data-id="element-114" />)}

            {/* Main Book Image */}
            <motion.div animate={{
            y: [-10, 10, -10]
          }} transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="relative z-10" data-id="element-115">
              <img src="https://cdn.magicpatterns.com/uploads/q3wyHuzTo6Qv9KsRecpsEn/Untitled_design_(1).png" alt="Colección Profesional de Perfiles de Aluminio — 3 manuales" className="w-full h-auto drop-shadow-[0_30px_80px_rgba(47,128,237,0.45)]" data-id="element-116" />
            </motion.div>
          </motion.div>

          {/* Promo Banner */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="mb-10" data-id="element-117">
            <div className="flex flex-col items-center gap-3" data-id="element-118">
              <div className="relative inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-[#2F80ED]/20 via-[#2F80ED]/10 to-[#2F80ED]/20 border border-[#2F80ED]/40 backdrop-blur-xl blue-glow" data-id="element-119">
                <span className="text-xl" data-id="element-120">🔥</span>
                <span className="text-white font-bold text-base md:text-lg tracking-tight whitespace-nowrap" data-id="element-121">
                  3x1 en manuales hoy solo por hoy
                </span>
              </div>

              <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-sm md:text-base font-semibold" data-id="element-122">
                <span data-id="element-123">⏰</span>
                <span className="whitespace-nowrap" data-id="element-124">
                  Oferta especial por tiempo limitado
                </span>
              </div>

              <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-red-500/10 border border-red-500/30 text-red-300 text-sm md:text-base font-semibold" data-id="element-125">
                <span data-id="element-126">⚠️</span>
                <span className="whitespace-nowrap" data-id="element-127">
                  Precio promocional disponible hoy
                </span>
              </div>
            </div>
          </motion.div>

          {/* Subheadline */}
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.5
        }} className="text-lg md:text-xl text-gray-400 mb-10 leading-relaxed max-w-3xl" data-id="element-128">
            La colección definitiva con sistemas, proyectos reales y estrategias
            profesionales para diseñar, construir y llevar tus resultados al
            siguiente nivel.
          </motion.p>

          {/* Bullets */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.7
        }} className="flex flex-wrap justify-center gap-x-6 gap-y-3 mb-12 max-w-3xl" data-id="element-129">
            {['Nivel 1: Fundamentos y aplicaciones', 'Nivel 2: Diseño y construcción profesional', 'Nivel 3: Negocio y proyectos avanzados', 'Bonus exclusivos incluidos'].map((item, i) => <div key={i} className="flex items-center gap-2 text-gray-300 text-sm md:text-base" data-id="element-130">
                <div className="w-5 h-5 rounded-full bg-[#2F80ED]/20 flex items-center justify-center shrink-0" data-id="element-131">
                  <Check className="w-3 h-3 text-[#2F80ED]" data-id="element-132" />
                </div>
                <span className="font-medium" data-id="element-133">{item}</span>
              </div>)}
          </motion.div>

          {/* CTAs */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.9
        }} className="flex flex-col sm:flex-row gap-4" data-id="element-134">
            <a href="https://checkout.librofy.online/cart/53265711235435:1" target="_blank" rel="noopener noreferrer" className="bg-[#2F80ED] hover:bg-[#2563EB] text-white px-8 py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2 blue-glow hover:scale-105" data-id="element-135">
              DESCARGAR AHORA
              <ArrowRight className="w-5 h-5" data-id="element-136" />
            </a>
            <a href="#niveles" className="glass-panel hover:bg-white/5 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all flex items-center justify-center" data-id="element-137">
              Ver contenido completo
            </a>
          </motion.div>
        </div>
      </div>
    </section>;
}